import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react'
import exenv from 'exenv'

const isClientSide = exenv.canUseDOM

const eventListenerOptions = {
	once: true,
	capture: true,
	passive: true,
}

const getDisplayName = (WrappedComponent) => {
	return WrappedComponent.displayName || WrappedComponent.name || 'Component'
}

const defaultHydrationStyle = { width: '100%', height: '100%' }

const useHydrationStyle = (wrapperProps, runTimeWrapperProps, on) => {
	return useMemo(() => {
		// display contents doesn't work with IntersectionObserver
		const style = on?.some((eventType) => (Array.isArray(eventType) ? eventType[0] : eventType) === 'visible')
			? defaultHydrationStyle
			: { ...defaultHydrationStyle, display: 'contents' } // default style to prevent layout to break before hydration
		return { ...style, ...wrapperProps?.style, ...runTimeWrapperProps?.style }
	}, [on, runTimeWrapperProps?.style, wrapperProps?.style])
}

const withHydrationOnDemandServerSide =
	({ on, wrapperProps }) =>
	(WrappedComponent) =>
		function HydrationOnDemandServerSide({ wrapperProps: runTimeWrapperProps, ...props }) {
			const style = useHydrationStyle(wrapperProps, runTimeWrapperProps, on)
			return (
				<section data-hydration-on-demand={true} {...wrapperProps} {...runTimeWrapperProps} style={style}>
					<WrappedComponent {...props} />
				</section>
			)
		}

const withHydrationOnDemandClientSide =
	({
		disableFallback = false,
		isInputPendingFallbackValue = true,
		on = [],
		onBefore,
		whenInputPending = false,
		wrapperProps,
	}) =>
	(WrappedComponent) => {
		const WithHydrationOnDemand = ({ forceHydration = false, wrapperProps: runTimeWrapperProps, ...props }) => {
			const rootRef = useRef(null)
			const cleanupFunctions = useRef([])
			const style = useHydrationStyle(wrapperProps, runTimeWrapperProps, on)

			const isInputPending = () => {
				const isInputPending = navigator?.scheduling?.isInputPending?.()
				return isInputPending ?? isInputPendingFallbackValue
			}

			const getDefaultHydrationState = () => {
				const isNotInputPending = whenInputPending && !isInputPending()
				return (isNotInputPending || forceHydration) && !onBefore
			}

			const [isHydrated, setIsHydrated] = useState(getDefaultHydrationState())

			const cleanUp = () => {
				cleanupFunctions.current.forEach((fn) => fn())
				cleanupFunctions.current = []
			}

			const hydrate = useCallback(async () => {
				cleanUp()
				if (isHydrated) return

				if (onBefore) await onBefore()
				setIsHydrated(true)
			}, [isHydrated])

			const initDOMEvent = useCallback(
				(type, getTarget = () => [rootRef.current]) => {
					const target = getTarget()
					const targets = typeof target?.[Symbol.iterator] === 'function' ? target : [target]
					targets.forEach((el) => {
						el?.addEventListener(type, hydrate, eventListenerOptions)
					})

					cleanupFunctions.current.push(() => {
						if (!targets) return
						targets.forEach((el) => {
							el?.removeEventListener(type, hydrate, eventListenerOptions)
						})
					})
				},
				[hydrate]
			)

			const initTimeout = useCallback(
				(delay = 2000) => {
					if (delay <= 0) return

					const timeout = setTimeout(hydrate, delay)
					cleanupFunctions.current.push(() => clearTimeout(timeout))
				},
				[hydrate]
			)

			const initIdleCallback = useCallback(() => {
				if (!('requestIdleCallback' in window)) {
					initTimeout()
					return
				}

				const idleCallback = requestIdleCallback(() => requestAnimationFrame(() => hydrate()), { timeout: 500 })

				if (!('cancelIdleCallback' in window)) return

				cleanupFunctions.current.push(() => {
					cancelIdleCallback(idleCallback)
				})
			}, [hydrate, initTimeout])

			const initIntersectionObserver = useCallback(
				(getOptions = Function.prototype) => {
					if (!('IntersectionObserver' in window)) {
						hydrate()
						return
					}

					const options = getOptions()
					const observer = new IntersectionObserver(([entry]) => {
						if (!entry.isIntersecting || !(entry.intersectionRatio > 0)) return

						hydrate()
					}, options)

					cleanupFunctions.current.push(() => {
						if (!observer) return
						observer.disconnect()
					})

					observer.observe(rootRef.current)
				},
				[hydrate]
			)

			const initEvent = useCallback(
				(type, options) => {
					switch (type) {
						case 'delay':
							initTimeout(options)
							break
						case 'visible':
							initIntersectionObserver(options)
							break
						case 'idle':
							initIdleCallback()
							break
						default:
							initDOMEvent(type, options)
					}
				},
				[initDOMEvent, initIdleCallback, initIntersectionObserver, initTimeout]
			)

			useLayoutEffect(() => {
				if (isHydrated) return

				if (forceHydration) {
					hydrate()
					return
				}

				const wasRenderedServerSide = !!rootRef.current.getAttribute('data-hydration-on-demand')
				const shouldHydrate = !wasRenderedServerSide && !disableFallback

				if (shouldHydrate) hydrate()
			}, [forceHydration, hydrate, isHydrated])

			useEffect(() => {
				if (isHydrated) return

				on.forEach((event) => (Array.isArray(event) ? initEvent(...event) : initEvent(event)))
				return cleanUp
			}, [initEvent, isHydrated])

			if (!isHydrated)
				return (
					<section
						ref={rootRef}
						dangerouslySetInnerHTML={{ __html: '' }}
						suppressHydrationWarning
						{...wrapperProps}
						{...runTimeWrapperProps}
						style={style}
					/>
				)

			// TODO testing if we could remove section once hydrated
			return (
				// <section {...wrapperProps} {...runTimeWrapperProps} style={style}>
				<WrappedComponent {...props} />
				// </section>
			)
		}

		WithHydrationOnDemand.displayName = `withHydrationOnDemand(${getDisplayName(WrappedComponent)})`

		return WithHydrationOnDemand
	}
const withHydrationOnDemand = (options = {}) => {
	if (isClientSide) return withHydrationOnDemandClientSide(options)

	return withHydrationOnDemandServerSide(options)
}

export default withHydrationOnDemand
